import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import Icon from '../../components/AppIcon';

const AuthBackground = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #e8e4f8 0%, #d4d8f5 25%, #c8d4f0 50%, #b8cef0 75%, #a8c8ee 100%)' }}>
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height:'45%'}}>
        <path fill="rgba(100,120,220,0.18)" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
      </svg>
      <svg className="absolute bottom-0 right-0 w-3/4" viewBox="0 0 800 400" preserveAspectRatio="none" style={{height:'55%'}}>
        <path fill="rgba(80,100,200,0.22)" d="M800,100L700,120C600,140,400,180,200,190C0,200,0,180,0,160L0,400L800,400Z"/>
      </svg>
      <div className="absolute top-8 right-12 opacity-20" style={{color:'#7c6fe0'}}>
        {[...Array(5)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(5)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
    </div>
    <div className="w-full max-w-md relative z-10">
      {children}
    </div>
  </div>
);

const OtpSetupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const emailAddress = location?.state?.email_address || '';

  useEffect(() => {
    setup2FA();
  }, []);

  const setup2FA = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService?.setup2FA({ email_address: emailAddress });
      const data = response?.data;
      if (data?.status_code === 200) {
        setQrCode(data?.qr_code || data?.qrCode || data?.qr || '');
      } else {
        setError('Failed to setup 2FA. Please try again.');
      }
    } catch (err) {
      setError('Failed to setup 2FA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}>
          <Icon name="Shield" size={30} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Setup Two-Factor Auth</h1>
        <p className="text-gray-500 mt-2 text-sm">Scan the QR code with your authenticator app</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/60">
        {loading ? (
          <div className="flex flex-col items-center py-10 space-y-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background:'#f5f3ff'}}>
              <Icon name="Loader2" size={24} className="animate-spin" style={{color:'#5b4fcf'}} />
            </div>
            <p className="text-gray-500 text-sm">Setting up 2FA...</p>
          </div>
        ) : error ? (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <Icon name="AlertCircle" size={16} className="text-red-500" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
            <button
              onClick={setup2FA}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{background:'linear-gradient(135deg,#5b4fcf,#4a6bc4)'}}>1</div>
                <h3 className="text-sm font-semibold text-gray-800">Install an Authenticator App</h3>
              </div>
              <p className="text-sm text-gray-500 ml-8">Download Google Authenticator, Authy, or any TOTP-compatible app.</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{background:'linear-gradient(135deg,#5b4fcf,#4a6bc4)'}}>2</div>
                <h3 className="text-sm font-semibold text-gray-800">Scan QR Code</h3>
              </div>
              {qrCode ? (
                <div className="flex justify-center p-4 bg-gray-50 rounded-xl border border-[#dde1f5] ml-8">
                  <img src={qrCode} alt="QR code for 2FA setup - scan with your authenticator app" className="w-44 h-44" />
                </div>
              ) : (
                <div className="flex justify-center p-4 bg-gray-50 rounded-xl border border-[#dde1f5] ml-8">
                  <div className="w-44 h-44 flex items-center justify-center">
                    <Icon name="QrCode" size={56} className="text-gray-300" />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{background:'linear-gradient(135deg,#5b4fcf,#4a6bc4)'}}>3</div>
                <h3 className="text-sm font-semibold text-gray-800">Continue to Verification</h3>
              </div>
              <p className="text-sm text-gray-500 ml-8">After scanning the QR code, click continue to verify your setup.</p>
            </div>

            <button
              onClick={() => navigate('/otp-verify', { state: { email_address: emailAddress } })}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
            >
              Continue to Verification
            </button>
          </div>
        )}
      </div>
    </AuthBackground>
  );
};

export default OtpSetupPage;
